
import _ from 'lodash'
import { createSelector } from 'reselect'

import { unmap as unmapAdmission } from '../lib/Proxy/admissions.js'
import { unmapGoal } from '../lib/Proxy/goal.js'
import { unmap as unmapCoverage } from '../lib/Proxy/insurance.js'
import { mapPatientToForm as unmapPatient } from '../lib/Proxy/patient.js'
import { unmap as unmapProcedure } from '../lib/Proxy/procedures.js'

/** **********************
 ** Admission <- Encounter
 ** ******************** */
const ADMISSION_CODES = [
  'inpatient_hospital',
  'ed',
  'inpatient_w_icu',
  'provider_appointment',
  'post_acute',
  'snf',
  'hospice'
]

/** **********************
 ** Allergy
 ** ******************** */
export const allergies = redux =>
  _.chain(redux)
    .get('patient.compartment.AllergyIntolerance', {})
    .values()
    .value()

/** **********************
 ** CareTeam
 ** ******************** */
export const careTeams = redux =>
  _.chain(redux).get('patient.compartment.CareTeam', {}).values().value()

/** **********************
 ** Coverage
 ** ******************** */
export const coverages = redux =>
  _.chain(redux).get('patient.compartment.Coverage', {}).values().value()

export const unmappedCoverages = createSelector(coverages, items =>
  items.map(coverage => coverage && unmapCoverage(coverage)))

/** **********************
 ** Encounter
 ** ******************** */
const allEncounters = redux =>
  _.chain(redux)
    .get('patient.compartment.Encounter', {})
    .values()
    .sort((a, b) => {
      const updatedAtA = _.get(a, 'meta.lastUpdated')
      const updatedAtB = _.get(b, 'meta.lastUpdated')
      if (updatedAtA > updatedAtB) {
        return 1
      }
      if (updatedAtA === updatedAtB) {
        return 0
      }
      return -1
    })
    .value()

export const encounters = createSelector(allEncounters, items => {
  if (!items) {
    return null
  }
  return items.filter(
    e => !ADMISSION_CODES.includes(_.get(e, 'type[0].coding[0].code'))
  )
})

/** **********************
 ** EpisodeOfCare
 ** ******************** */
export const episodeOfCare = redux =>
  _.chain(redux)
    .get('patient.compartment.EpisodeOfCare', {})
    .values()
    .first()
    .value()

/** **********************
 ** Goal
 ** ******************** */
const GOAL_PERSONAL_MILESTONE_CODE = '160786009'

export const goals = redux =>
  _.chain(redux)
    .get('patient.compartment.Goal', {})
    .values()
    .filter(
      g =>
        _.get(g, 'category[0].coding[0].code') === GOAL_PERSONAL_MILESTONE_CODE
    )
    .value()

export const unmappedGoals = createSelector(goals, items =>
  items.map(unmapGoal))

export const carePlans = redux =>
  _.chain(redux)
    .get('patient.compartment.CarePlan', {})
    .values()
    .filter(cp => [ 'not-started', 'in-progress' ].includes(cp.status))
    .value()

let emptyObject = {}

/** **********************
 ** Observation
 ** ******************** */
export const observations = createSelector(
  [ state => state.patient?.compartment?.Observation ?? emptyObject ],
  observationsMap => Object.values(observationsMap)
)

/** **********************
 ** Patient
 ** ******************** */
export const patient = redux =>
  _.chain(redux)
    .get('patient.compartment.Patient', {})
    .values()
    .first()
    .value()

export const unmappedPatient = createSelector(
  patient,
  item => item && unmapPatient(item)
)

/** **********************
 ** Practitioner
 ** ******************** */
export const practitioners = redux =>
  _.chain(redux).get('patient.compartment.Practitioner', {}).values().value()

/** **********************
 ** Procedure
 ** ******************** */
export const procedures = redux =>
  _.chain(redux).get('patient.compartment.Procedure', {}).values().value()

export const unmappedProcedures = createSelector(procedures, items =>
  items.map(p => unmapProcedure(p)))

/** **********************
 ** QuestionaireResponse
 ** ******************** */
export const questionnaireResponses = redux =>
  _.chain(redux)
    .get('patient.compartment.QuestionnaireResponse', {})
    .values()
    .value()

export const admissions = createSelector(allEncounters, items => {
  if (!items) {
    return null
  }
  return items.filter(e =>
    ADMISSION_CODES.includes(_.get(e, 'type[0].coding[0].code')))
})

export const unmappedAdmissions = createSelector(admissions, items =>
  items.map(p => unmapAdmission(p)))
